import {useCallback, useEffect, useRef, useState} from 'react';
import scrollStyles from '../styles/scrollbar.module.css';

const DEFAULT_HIDE_DELAY_MS = 1200;

/**
 * Scrollbar fina visível só durante o scroll; some após inatividade.
 * @param {React.MutableRefObject<HTMLElement | null>} [externalRef]
 * @param {{ hideDelayMs?: number, orientation?: 'vertical' | 'horizontal' }} [options]
 */
export function useAutoHideScrollbar(
  externalRef,
  {hideDelayMs = DEFAULT_HIDE_DELAY_MS, orientation = 'vertical'} = {},
) {
  const [scrollElement, setScrollElement] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = useRef(null);

  const activeClassName =
    orientation === 'horizontal'
      ? scrollStyles.campaignChatScrollHorizontalActive
      : scrollStyles.campaignChatScrollActive;

  const ref = useCallback(
    (node) => {
      setScrollElement(node);
      if (externalRef) {
        externalRef.current = node;
      }
    },
    [externalRef],
  );

  const revealScrollbar = useCallback(() => {
    setIsVisible(true);

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      hideTimeoutRef.current = null;
    }, hideDelayMs);
  }, [hideDelayMs]);

  useEffect(() => {
    if (!scrollElement) {
      return undefined;
    }

    scrollElement.addEventListener('scroll', revealScrollbar, {passive: true});

    return () => {
      scrollElement.removeEventListener('scroll', revealScrollbar);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [scrollElement, revealScrollbar]);

  return {
    ref,
    scrollClassName: isVisible ? activeClassName : '',
  };
}
